import React, { Fragment } from 'react';
import { observer, inject } from 'mobx-react';
import {
  withStyles,
  Grid,
  DialogActions,
  Dialog,
  Button,
  DialogContent,
  DialogTitle,
  TextField,
  FormHelperText,
  InputAdornment,
  Typography,
} from '@material-ui/core';
import Swal from 'sweetalert2';
import './styles/dialog.css';
import './styles/patientview.css';
import { withTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';
import moment from 'moment';
import { getDateTimeFormatted } from '../../utils/timeFormatted';
import TimeField from 'react-simple-timefield';
import config from '../../config';

class VitalSigns extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      createdDate: this.props.date,
      time: this.props.time,
      heartrate: 0,
      temperature: 0,
      spo2: 0,
      respirationRate: 0,
      bloodPressure: {
        systolic: 0,
        diastolic: 0,
      },
      errorField: {
        createdDate: false,
        heartrate: false,
        temperature: false,
        spo2: false,
        respirationRate: false,
        bloodPressure: false,
      },
      errorReason: {
        createdDate: '',
      },
    };
  }

  onSubmit = async (patientId) => {
    const { time, createdDate, heartrate, temperature, spo2, respirationRate, bloodPressure } = this.state;
    const { t } = this.props;
    const dateTime = `${createdDate} ${time}`;
    const date = getDateTimeFormatted(dateTime);
    const valid = this.validate();
    let vitalSignData = {
      patientId,
      createdDate: new Date(date).toISOString(),
    };

    if (valid) {
      try {
        if (heartrate || bloodPressure.diastolic || bloodPressure.systolic || temperature || respirationRate || spo2) {
          if (heartrate) {
            vitalSignData['heartrate'] = heartrate;
            this.props.vitalsign.createHeartRate(patientId, date, heartrate);
          }
          if (bloodPressure.diastolic && bloodPressure.systolic) {
            vitalSignData['bloodPressure'] = bloodPressure;
            this.props.vitalsign.createBloodPressure(patientId, date, bloodPressure);
          }
          if (temperature) {
            vitalSignData['temperature'] = temperature;
            this.props.vitalsign.createTemperature(patientId, date, temperature);
          }
          if (spo2) {
            vitalSignData['spo2'] = spo2;
            this.props.vitalsign.createSpo2(patientId, date, spo2);
          }
          if (respirationRate) {
            vitalSignData['respirationRate'] = respirationRate;
            this.props.vitalsign.createRespirationRate(patientId, date, respirationRate);
          }
          
          const nirun = config.features.allPatients.syncNirun;
          if (nirun) {
            this.props.nirun.createVitalSignForNirun(vitalSignData);
          }
          Swal.fire({
            type: 'success',
            html: `<h4>${t('create_success')}`,
            showConfirmButton: false,
            timer: 500,
            customClass: {
              container: 'my-swal',
            },
          }).then(() => {
            this.setState({
              heartrate: '',
              temperature: '',
              spo2: '',
              respirationRate: '',
              bloodPressure: {
                systolic: '',
                diastolic: '',
              },
            });
          });
        }
      } catch (err) {
        if (err.statusCode === 401) {
          window.location.href = '/login';
        } else if (err.statusCode === 400) {
          Swal.fire({
            type: 'error',
            html: `<h4>${err.error.message}`,
            customClass: {
              container: 'my-swal',
            },
          });
        }
      }
    }
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.clearErrorField(name);
    this.setState({ [name]: value });
  };

  handleChangeBloodPressure = (e) => {
    const { name, value } = e.target;
    this.clearErrorField(name);
    this.setState({
      bloodPressure: {
        ...this.state.bloodPressure,
        [name]: value,
      },
    });
  };

  clearErrorField(key) {
    if (key === 'createdDate') {
      this.setState({
        ...this.state,
        errorField: {
          ...this.state.errorField,
          createdDate: false,
        },
      });
    }
    if (key === 'temperature') {
      this.setState({
        ...this.state,
        errorField: {
          ...this.state.errorField,
          temperature: false,
        },
      });
    }
    if (key === 'systolic' || key === 'diastolic') {
      this.setState({
        ...this.state,
        errorField: {
          ...this.state.errorField,
          bloodPressure: false,
        },
      });
    }
    if (key === 'heartrate') {
      this.setState({
        ...this.state,
        errorField: {
          ...this.state.errorField,
          heartrate: false,
        },
      });
    }
    if (key === 'spo2') {
      this.setState({
        ...this.state,
        errorField: {
          ...this.state.errorField,
          spo2: false,
        },
      });
    }
    if (key === 'respirationRate') {
      this.setState({
        ...this.state,
        errorField: {
          ...this.state.errorField,
          respirationRate: false,
        },
      });
    }
  }

  validate() {
    const dateTime = this.validateDateTime();
    const checkTemperature = this.validateTemperature();
    const checkBloodPressure = this.validateBloodPressure();
    const checkHeartRate = this.validateHeartRate();
    const checkSpo2 = this.validateSpo2();
    const checkRespirationRate = this.validateRespirationRate();
    this.setState({
      errorField: {
        createdDate: dateTime.checkDate,
        temperature: checkTemperature,
        bloodPressure: checkBloodPressure,
        heartrate: checkHeartRate,
        spo2: checkSpo2,
        respirationRate: checkRespirationRate,
      },
      errorReason: {
        createdDate: dateTime.dateErrorReason,
      },
    });
    return !(
      dateTime.checkDate ||
      checkTemperature ||
      checkHeartRate ||
      checkSpo2 ||
      checkBloodPressure ||
      checkRespirationRate
    );
  }

  validateDateTime() {
    const { t } = this.props;
    const { createdDate, time } = this.state;

    let dateTime = `${createdDate} ${time}`;
    const date = dateTime ? dateTime.split(' ') : null;
    const newDate = date[0].split('/');
    const _year = newDate ? +newDate[2] - 543 : '0000';
    const _month = newDate ? newDate[1] : '00';
    const _day = newDate ? newDate[0] : '00';
    dateTime = `${_year}-${_month}-${_day}`;

    const times = date[1].split(':');
    const hours = times[0];
    const minutes = times[1];

    const dateNow = new Date();
    const thisYear = dateNow.getUTCFullYear();
    const minYear = thisYear - 120;

    const patternDate = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/;
    const currentDate = moment(new Date()).toDate().getTime();

    let checkDate = false;
    let dateErrorReason = '';
    if (
      dateTime === '0000-00-00' ||
      !dateTime ||
      dateTime === '' ||
      dateTime === undefined ||
      !patternDate.test(dateTime) ||
      moment().diff(dateTime, 'days') < 0 ||
      new Date(`${dateTime} ${date[1]}`).getTime() > currentDate
    ) {
      if (dateTime === '0000-00-00' || !dateTime || dateTime === '' || dateTime === undefined) {
        checkDate = true;
        dateErrorReason = `${t('required_field')}`;
      } else if (
        !patternDate.test(dateTime) ||
        moment().diff(dateTime, 'days') < 0 ||
        new Date(`${dateTime} ${date[1]}`).getTime() > currentDate
      ) {
        checkDate = true;
        dateErrorReason = `${t('invalid_format')}`;
      }
      return { checkDate, dateErrorReason }; //false;
    } else if (+_day < 1 || +_day > 31 || +_month < 1 || +_month > 12 || _year > thisYear || _year < minYear) {
      return { checkDate: true, dateErrorReason: `${t('invalid_format')}` }; //false;
    } else if ((+_month === 4 || +_month === 6 || +_month === 9 || +_month === 11) && +_day === 31) {
      return { checkDate: true, dateErrorReason: `${t('invalid_date')}` }; //false;
    } else if (+_month === 2) {
      const isleap = _year % 4 === 0 && (_year % 100 !== 0 || _year % 400 === 0);
      if (+_day > 29 || (+_day === 29 && !isleap)) {
        return { checkDate: true, dateErrorReason: `${t('invalid_date')}` }; //false;
      }
      return { checkDate: false, dateErrorReason: '' }; //true;
    } else if (hours > 24 || minutes > 59) {
      return { checkDate: true, dateErrorReason: `${t('invalid_date')}` }; //false;
    } else {
      return { checkDate: false, dateErrorReason: '' };
    }
  }

  validateTemperature() {
    let { temperature } = this.state;
    const numTemperature = Number(temperature);
    return numTemperature > 99;
  }

  validateHeartRate() {
    let { heartrate } = this.state;
    const numHeartrate = Number(heartrate);
    return numHeartrate > 220;
  }

  validateBloodPressure() {
    let { bloodPressure } = this.state;
    const diastolic = Number(bloodPressure.diastolic);
    const systolic = Number(bloodPressure.systolic);
    return diastolic > 500 || systolic > 500;
  }

  validateSpo2() {
    let { spo2 } = this.state;
    const numSpo2 = Number(spo2);
    return numSpo2 > 100;
  }

  validateRespirationRate() {
    let { respirationRate } = this.state;
    const numRespirationRate = Number(respirationRate);
    return numRespirationRate > 50;
  }

  getFullname = (language, data) => {
    let fullname = '';
    if (language && language === 'th-TH') {
      if (data.firstname || data.lastname) {
        fullname = `${data.firstname ?? ''} ${data.lastname ?? ''}`;
      } else {
        fullname = `${data.firstnameEn ?? ''} ${data.lastnameEn ?? ''}`;
      }
    }

    if (language && language === 'en-EN') {
      if (data.firstnameEn || data.lastnameEn) {
        fullname = `${data.firstnameEn ?? ''} ${data.lastnameEn ?? ''}`;
      } else {
        fullname = `${data.firstname ?? ''} ${data.lastname ?? ''}`;
      }
    }

    return fullname;
  };

  render() {
    const { t, classes } = this.props;
    const { time, createdDate, heartrate, temperature, spo2, respirationRate, bloodPressure } = this.state;
    const patient = this.props.data ? this.props.data.patient : this.props.patientData;
    const fullname = this.getFullname(localStorage.getItem('i18nextLng'), patient);
    const checkButton =
      !heartrate && !bloodPressure.diastolic && !bloodPressure.systolic && !temperature && !respirationRate && !spo2;

    return (
      <Dialog
        open={this.props.open}
        onClose={this.props.handleClose}
        aria-labelledby='form-dialog-title'
        fullWidth={true}
        PaperProps={{
          style: {
            maxWidth: '770px',
            padding: 4,
            borderRadius: 8,
          },
        }}
      >
        <DialogTitle id='form-dialog-title' style={{ padding: '16px 24px 5px' }}>
          <Grid container className={classes.gridTitle}>
            <div>
              {t('vitalsign_for')} <span className='headerMain'>{fullname}</span>
            </div>
            <div id='closeIcon' className={classes.closeIcon} onClick={this.props.handleClose}>
              <svg width='32' height='32' viewBox='0 0 32 32'>
                <use xlinkHref={`/images/menus-v1.svg#close`} />
              </svg>
            </div>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid container item className={classes.wrapContent}>
              <Grid container item md={12} style={{ marginBottom: 10 }}>
                <Typography className={classes.textSize}>{t('date_and_time')}</Typography>
                <Typography
                  variant='caption'
                  style={{
                    marginLeft: 5,
                    marginTop: 7,
                  }}
                >
                  {t('eg_be_andtime')}
                </Typography>
              </Grid>

              <Grid container item md={6} sm={6}>
                <div style={{ marginRight: 15 }}>
                  <svg width='32' height='32' viewBox='0 0 32 32'>
                    <use xlinkHref={`/images/date.svg#calendar`} />
                  </svg>
                </div>
                <Fragment>
                  <InputMask
                    id='date'
                    name='createdDate'
                    type='text'
                    mask='99/99/9999'
                    maskChar='_'
                    alwaysShowMask
                    value={createdDate}
                    onChange={this.handleChange}
                  >
                    {(inputProps) => (
                      <div>
                        <TextField
                          {...inputProps}
                          // error={this.state.errorField.createdDate}
                          className='birthdayControl'
                          style={{ width: '75%' }}
                        />
                        {/* <FormHelperText
                          error
                          hidden={!this.state.errorField.createdDate}
                          id="component-error-text"
                        >
                          {this.state.errorReason.createdDate}
                        </FormHelperText> */}
                      </div>
                    )}
                  </InputMask>
                </Fragment>
              </Grid>

              <Grid container item md={6} sm={6}>
                <div style={{ marginRight: 15 }}>
                  <svg width='33' height='32' viewBox='0 0 33 32'>
                    <use xlinkHref={`/images/date.svg#time`} />
                  </svg>
                </div>
                <Fragment>
                  <TimeField
                    // showSeconds
                    id='time'
                    name='time'
                    value={time}
                    onChange={this.handleChange}
                    input={
                      <TextField
                        id='time'
                        InputLabelProps={{
                          shrink: true,
                        }}
                        className='birthdayControl'
                        value={time}
                      />
                    }
                  />
                </Fragment>
              </Grid>

              <Grid container item md={12}>
                {this.state.errorField.createdDate && (
                  <Typography
                    variant='caption'
                    style={{
                      color: '#EB5861',
                      marginTop: 5,
                    }}
                  >
                    {this.state.errorReason.createdDate}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Grid container item md={4} xs={4}>
              <div className={classes.wrapPaper}>
                <div className={classes.wrapIcon}>
                  <svg width='40' height='40' viewBox='0 0 40 40'>
                    <use xlinkHref={`/images/vitalsign-v2.svg#temperature`} />
                  </svg>
                </div>
                <div>
                  <Typography className={classes.textSize}>{t('temperature')}</Typography>
                  <Fragment>
                    <InputMask
                      id='temperature'
                      name='temperature'
                      mask='99.9'
                      maskChar=''
                      alwaysShowMask
                      value={temperature || ''}
                      onChange={this.handleChange}
                    >
                      {(inputProps) => (
                        <div>
                          <TextField
                            {...inputProps}
                            error={this.state.errorField.temperature}
                            fullWidth
                            InputProps={{
                              classes: {
                                underline: classes.textFieldUnderline,
                              },
                              endAdornment: (
                                <InputAdornment position='end' className={classes.inputAdornme}>
                                  {' '}
                                  {t('celsius')}
                                </InputAdornment>
                              ),
                            }}
                          />
                          <FormHelperText error hidden={!this.state.errorField.temperature} id='component-error-text'>
                            {this.state.errorField.temperature ? `${t('this_value_is_invalid')}` : ''}
                          </FormHelperText>
                        </div>
                      )}
                    </InputMask>
                  </Fragment>
                </div>
              </div>
            </Grid>

            <Grid container item md={4} xs={4}>
              <div className={classes.wrapPaper}>
                <div className={classes.wrapIcon}>
                  <svg width='40' height='40' viewBox='0 0 40 40'>
                    <use xlinkHref={`/images/vitalsign-v2.svg#bloodpressure`} />
                  </svg>
                </div>
                <div>
                  <Typography className={classes.textSize}>{t('blood_pressure')}</Typography>
                  <div style={{ display: 'flex' }}>
                    <TextField
                      id='systolic'
                      name='systolic'
                      value={bloodPressure.systolic || ''}
                      onChange={this.handleChangeBloodPressure}
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                      }}
                      className={classes.inputSize}
                    />
                    <FormHelperText error hidden={!this.state.errorField.bloodPressure} id='component-error-text' />
                    <div style={{ fontSize: 25 }}>/</div>
                    <TextField
                      id='diastolic'
                      name='diastolic'
                      className={classes.mLeft}
                      value={bloodPressure.diastolic || ''}
                      onChange={this.handleChangeBloodPressure}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end' className={classes.inputAdornme}>
                            {' '}
                            {t('mmhg')}
                          </InputAdornment>
                        ),
                      }}
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                      }}
                    />
                  </div>
                  <FormHelperText
                    error
                    hidden={!this.state.errorField.bloodPressure}
                    id='component-error-text'
                    style={{ marginTop: 3 }}
                  >
                    {this.state.errorField.bloodPressure ? `${t('this_value_is_invalid')}` : ''}
                  </FormHelperText>
                </div>
              </div>
            </Grid>

            <Grid container item md={4} xs={4}>
              <div className={classes.wrapPaper}>
                <div className={classes.wrapIcon}>
                  <svg width='40' height='40' viewBox='0 0 40 40'>
                    <use xlinkHref={`/images/vitalsign-v2.svg#heartrate`} />
                  </svg>
                </div>
                <div>
                  <Typography className={classes.textSize}>{t('heart_rate')}</Typography>
                  <TextField
                    id='heartrate'
                    name='heartrate'
                    value={heartrate || ''}
                    onChange={this.handleChange}
                    className={classes.mBottom}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end' className={classes.inputAdornme}>
                          {t('bpm')}
                        </InputAdornment>
                      ),
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                    }}
                  />
                  <FormHelperText error hidden={!this.state.errorField.heartrate} id='component-error-text'>
                    {this.state.errorField.heartrate ? `${t('this_value_is_invalid')}` : ''}
                  </FormHelperText>
                </div>
              </div>
            </Grid>

            <Grid container item md={4} xs={4}>
              <div className={classes.wrapPaper}>
                <div className={classes.wrapIcon}>
                  <svg width='40' height='40' viewBox='0 0 40 40'>
                    <use xlinkHref={`/images/vitalsign-v2.svg#oxygen`} />
                  </svg>
                </div>
                <div>
                  <Typography className={classes.textSize}>{t('spo2')}</Typography>
                  <TextField
                    id='spo2'
                    name='spo2'
                    value={spo2 || ''}
                    onChange={this.handleChange}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                    }}
                  />
                  <FormHelperText error hidden={!this.state.errorField.spo2} id='component-error-text'>
                    {this.state.errorField.spo2 ? `${t('this_value_is_invalid')}` : ''}
                  </FormHelperText>
                </div>
              </div>
            </Grid>

            <Grid container item md={4} xs={4}>
              <div className={classes.wrapPaper}>
                <div className={classes.wrapIcon}>
                  <svg width='40' height='40' viewBox='0 0 40 40'>
                    <use xlinkHref={`/images/vitalsign-v2.svg#respirationrate`} />
                  </svg>
                </div>
                <div>
                  <Typography className={classes.textSize}>{t('respiratory_rate')}</Typography>
                  <TextField
                    id='respirationRate'
                    name='respirationRate'
                    value={respirationRate || ''}
                    onChange={this.handleChange}
                    className={classes.mBottom}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end' className={classes.inputAdornme}>
                          {t('bpm')}
                        </InputAdornment>
                      ),
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                    }}
                  />
                  <FormHelperText error hidden={!this.state.errorField.respirationRate} id='component-error-text'>
                    {this.state.errorField.respirationRate ? `${t('this_value_is_invalid')}` : ''}
                  </FormHelperText>
                </div>
              </div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {/* <Button color="default" onClick={this.props.handleClose}>
            {t("close")}
          </Button> */}
          <Grid
            container
            item
            style={{
              margin: '10px 28px',
              justifyContent: 'right',
            }}
          >
            <Button
              id='save'
              color='primary'
              variant='contained'
              onClick={() => this.onSubmit(patient.id)}
              disabled={checkButton}
              style={{
                width: 100,
              }}
            >
              {t('save')}
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  }
}

const styles = (theme) => ({
  gridTitle: {
    margin: 10,
    position: 'relative',
  },
  wrapContent: {
    padding: 20,
    margin: 10,
    border: '1px solid #D7D7D7',
    borderRadius: 10,
    height: 120,
  },
  wrapIcon: {
    // display: "flex",
    // alignItems: "center",
    // justifyContent: "center",
    // width: "30%",
  },
  wrapPaper: {
    // display: "flex",
    width: '100%',
    border: '1px solid #D7D7D7',
    borderRadius: '10px',
    margin: 10,
    padding: '20px 14px 20px 16px',
    height: 150,
  },
  inputSize: {
    width: '50%',
  },
  inputAdornme: {
    fontSize: 14,
    display: 'flex',
    justifyContent: 'end',
    width: '100%',
    backgroundColor: 'transparent !important',
  },
  mBottom: {
    marginBottom: 4,
  },
  textSize: {
    fontSize: 20,
    fontWeight: 500,
    color: theme.palette.text.header,
  },
  mLeft: {
    marginLeft: 4,
  },
  textFieldUnderline: {
    '&:after': {
      borderBottomColor: `${theme.palette.text.primary} !important`,
      borderBottomWidth: '1px !important',
      '&:hover': {
        borderBottomColor: `${theme.palette.text.primary} !important`,
      },
    },
  },
  closeIcon: {
    position: 'absolute',
    right: 18,
    cursor: 'pointer',
  },
});

export const page = inject('vitalsign', 'nirun')(observer(VitalSigns));
export const translation = withTranslation()(page);
export default withStyles(styles, { withTheme: true })(translation);
